import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { getReorderFlow } from "../services/api";
import { fmtDate } from "../utils/buyerMapping";
import { ui, ErrorState, EmptyState } from "./common/ui";

// 2026-10-12 Reorder flow strip (FLOW_CONTRACT.md "Flow strip"): one card per workflow stage for a session (or all open
// sessions), Raised -> Buyer decision -> CP -> PO -> GRN -> Expired (vendor stages dropped 2026-10-20). Data comes from
// GET /api/reorder/flow (computed server-side, nothing stored). Parts flagged `pending` are the work still waiting and
// open the page where it gets done via onNavigate(target) - App.js owns the view switch.

const n = (v) => (Number(v) || 0).toLocaleString("en-IN");
const MS_DAY = 86400000;

// Where a pending part sends the user. session = a session id, or null for "all".
export function flowTarget(stageKey, partKey, session) {
  if (stageKey === "buyer" && partKey === "not_actioned") return session ? { view: "detail", sessionId: session } : { view: "consolidated" };
  if (stageKey === "cp" && partKey === "pending") return { view: "cp-approvals", sessionId: session };
  if (stageKey === "po" && partKey === "not_yet") return session ? { view: "detail", sessionId: session, statusFilter: "pending" } : { view: "consolidated" };
  if (stageKey === "dispatch" && partKey === "not_sent") return { view: "vendor-pdfs", sessionId: session };
  if (stageKey === "vendor" && partKey === "no_response") return { view: "vendor-responses" };
  if (stageKey === "grn" && partKey === "none") return { view: "consolidated" };
  if (stageKey === "expired") return { view: "expired" };
  return null;
}

// "12 Oct 2026 · 2 days old" for the PO file the numbers were computed from (daily feed: > 3 days old = stale).
function fileAge(dateStr) {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).includes("T") ? dateStr : dateStr + "T00:00:00");
  if (isNaN(d)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const days = Math.max(0, Math.round((today - day) / MS_DAY));
  return { label: `${fmtDate(dateStr)} · ${days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"} old`}`, stale: days > 3 };
}

const latestSessionId = (list) => {
  const s = (list || []).filter((x) => x && x._id && !x.archived)
    .slice().sort((a, b) => String(b.request_date || b.upload_date || "").localeCompare(String(a.request_date || a.upload_date || "")))[0];
  return s ? s._id : "all";
};

const CSS = `
.fs-row { display:flex; align-items:stretch; gap:6px; flex-wrap:wrap; }
.fs-card { flex:1 1 150px; min-width:140px; padding:10px 12px; display:flex; flex-direction:column; gap:6px; }
.fs-arrow { align-self:center; color:var(--text-muted); font-size:14px; flex:0 0 auto; }
.fs-part { display:flex; justify-content:space-between; align-items:baseline; gap:8px; font-size:12.5px; line-height:1.35; width:100%; background:none; border:none; padding:2px 4px; margin:0 -4px; border-radius:4px; text-align:left; color:var(--text); font-family:inherit; }
.fs-part .fs-v { font-variant-numeric:tabular-nums; white-space:nowrap; color:var(--text-muted); }
.fs-part.pending, .fs-part.pending .fs-v { color:var(--danger); font-weight:600; }
button.fs-part { cursor:pointer; transition: background var(--t-fast); }
button.fs-part:hover { background: var(--danger-bg); }
button.fs-part:focus-visible { outline:none; box-shadow: var(--focus-ring); }
.fs-foot { font-size:11.5px; color:var(--text-muted); line-height:1.4; overflow-wrap:anywhere; }
.fs-skel { height:10px; border-radius:4px; background:linear-gradient(90deg, var(--bg-hover) 0%, var(--border) 50%, var(--bg-hover) 100%); background-size:800px 100%; animation: ui-shimmer 1.2s linear infinite; }
@media (max-width: 640px) { .fs-arrow { display:none; } .fs-card { flex:1 1 calc(50% - 6px); min-width:0; padding:8px 10px; } .fs-part { font-size:12px; } }
`;

function Part({ stageKey, part, session, onNavigate, canOpen }) {
  const target = part.pending ? flowTarget(stageKey, part.key, session) : null;
  const clickable = !!(target && onNavigate && (!canOpen || canOpen(target.view) !== false));
  const body = (
    <>
      <span>{part.label}{clickable ? " →" : ""}</span>
      <span className="fs-v">{n(part.styles)} · {n(part.qty)}</span>
    </>
  );
  const title = part.qty_original != null && part.qty_original !== part.qty ? `Originally raised: ${n(part.qty_original)} qty` : undefined;
  if (clickable) {
    return <button type="button" className="fs-part pending" data-part={`${stageKey}.${part.key}`} title={title || "Open where this gets done"} onClick={() => onNavigate(target)}>{body}</button>;
  }
  return <div className={`fs-part${part.pending ? " pending" : ""}`} data-part={`${stageKey}.${part.key}`} title={title}>{body}</div>;
}

function StageCard({ stage, session, onNavigate, canOpen }) {
  const parts = stage.parts || [];
  let foot = null;
  if (stage.key === "expired" && !session) {
    // Expired POs are joined by style only (backend note 2026-10-12): across sessions a style counts in each one.
    foot = "A style in two sessions counts its expired POs in both.";
  }
  const expiredTarget = stage.key === "expired" ? flowTarget("expired", null, session) : null;
  const expiredClickable = expiredTarget && onNavigate && (stage.styles || 0) > 0;
  return (
    <section className="fs-card" style={ui.card} data-stage={stage.key} title={stage.basis ? `Counted over: ${stage.basis}` : undefined}>
      <div style={{ fontSize: 11.5, fontWeight: 650, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-muted)" }}>{stage.label}</div>
      {!parts.length && stage.key !== "expired" && (
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>
          {n(stage.styles)} <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)" }}>styles · {n(stage.qty)} qty</span>
          {stage.qty_original != null && stage.qty_original !== stage.qty && (
            <div style={{ fontSize: 11.5, fontWeight: 500, color: "var(--text-muted)" }}>Originally {n(stage.qty_original)} qty</div>
          )}
        </div>
      )}
      {stage.key === "expired" && (
        expiredClickable
          ? <button type="button" className="fs-part pending" data-part="expired" onClick={() => onNavigate(expiredTarget)} title="Open Expired POs">
              <span>{n(stage.pos)} POs →</span><span className="fs-v">{n(stage.styles)} · {n(stage.qty)}</span>
            </button>
          : <div className="fs-part" data-part="expired"><span>{n(stage.pos)} POs</span><span className="fs-v">{n(stage.styles)} · {n(stage.qty)}</span></div>
      )}
      {parts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {parts.map((p) => <Part key={p.key} stageKey={stage.key} part={p} session={session} onNavigate={onNavigate} canOpen={canOpen} />)}
        </div>
      )}
      {foot && <div className="fs-foot" data-foot={stage.key}>{foot}</div>}
    </section>
  );
}

function Skeleton() {
  return (
    <div className="fs-row" aria-busy="true" aria-label="Loading reorder flow">
      {Array.from({ length: 6 }).map((_, i) => (
        <section key={i} className="fs-card" style={ui.card}>
          <div className="fs-skel" style={{ width: "60%" }} />
          <div className="fs-skel" style={{ width: "90%" }} />
          <div className="fs-skel" style={{ width: "75%" }} />
        </section>
      ))}
    </div>
  );
}

export default function FlowStrip({ sessions: overallSessions = [], onNavigate, canOpen, initialSession }) {
  const [sessionId, setSessionId] = useState(() => initialSession || latestSessionId(overallSessions));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const reqRef = useRef(0);

  useEffect(() => {
    const id = ++reqRef.current;
    setLoading(true); setError(null);
    getReorderFlow(sessionId)
      .then((d) => { if (id === reqRef.current) setData(d || null); })
      .catch((e) => {
        if (id !== reqRef.current) return;
        console.error("[flow] load failed", e);
        setError(e?.response?.data?.detail || "Could not load the reorder flow.");
      })
      .finally(() => { if (id === reqRef.current) setLoading(false); });
  }, [sessionId, tick]);
  const retry = useCallback(() => setTick((t) => t + 1), []);

  const pickerSessions = useMemo(() => {
    const list = (data && data.sessions) || overallSessions.map((s) => ({ _id: s._id, label: `${fmtDate(s.request_date)} · ${n(s.total_styles)} styles`, archived: false }));
    return list.filter((s) => s && s._id && !s.archived);
  }, [data, overallSessions]);

  const session = sessionId === "all" ? null : sessionId;
  const stages = (data && data.stages) || [];
  const age = fileAge(data && data.as_of && data.as_of.po_file_date);

  return (
    <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }} data-flow-strip="">
      <style>{CSS}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ fontSize: 14, fontWeight: 650, color: "var(--text)", margin: 0 }}>Reorder flow</h3>
        <select aria-label="Session" value={sessionId} onChange={(e) => setSessionId(e.target.value)} style={{ ...ui.input, padding: "5px 8px", fontSize: 12.5, maxWidth: "100%" }}>
          {pickerSessions.map((s) => <option key={s._id} value={s._id}>{s.label || `${fmtDate(s.request_date)} · ${n(s.total_styles)} styles`}</option>)}
          {!pickerSessions.some((s) => s._id === sessionId) && sessionId !== "all" && <option value={sessionId}>Selected session</option>}
          <option value="all">All open sessions</option>
        </select>
        {data && data.as_of && (data.as_of.po_file || age) && (
          <span className="fs-foot" data-foot="as_of" style={age && age.stale ? { color: "var(--warning)", fontWeight: 600 } : undefined}
            title={age && age.stale ? "PO file is more than 3 days old - the daily refresh may have failed" : undefined}>
            PO file{data.as_of.po_file ? `: ${data.as_of.po_file}` : ""}{age ? ` · ${age.label}` : ""}
          </span>
        )}
        {sessionId === "all" && data && <span className="fs-foot">A style in two sessions counts once per session.</span>}
      </div>
      {loading && !data && <Skeleton />}
      {error && <ErrorState message={error} onRetry={retry} />}
      {!error && !loading && data && !stages.length && <div style={ui.card}><EmptyState title="No open reorder sessions" /></div>}
      {!error && data && stages.length > 0 && (
        <div className="fs-row" style={loading ? { opacity: 0.6, transition: "opacity var(--t-fast)" } : undefined}>
          {stages.map((st, i) => (
            <React.Fragment key={st.key}>
              {i > 0 && <span className="fs-arrow" aria-hidden="true">→</span>}
              <StageCard stage={st} session={session} onNavigate={onNavigate} canOpen={canOpen} />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
